import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/lib/auth'
import { discountClient } from './discount-client'
import type { DiscountPreviewInput, DiscountSchedule, DiscountScheduleInput } from './discount-types'

export const discountKeys = {
  all: (eventId: number) => ['discounts', eventId] as const,
  schedules: (eventId: number, includeArchived = false) => ['discounts', eventId, 'schedules', includeArchived] as const,
  scope: (eventId: number) => ['discounts', eventId, 'scope'] as const,
  report: (eventId: number) => ['discounts', eventId, 'report'] as const,
  fallbackOrders: (eventId: number) => ['discounts', eventId, 'fallback-orders'] as const,
  // The hooks below only know the schedule or item id, so these sit outside
  // the event's key; mutations invalidate them by prefix.
  schedule: (scheduleId: number) => ['discount-schedule', scheduleId] as const,
  itemDiscounts: (itemId: number) => ['item-discounts', itemId] as const,
  // Under 'menuItems' so price changes (schedule saves, menu item edits) refresh it.
  targetItems: (eventId: number) => ['menuItems', 'discount-targets', eventId] as const,
  // Under 'vendorEvents' (queries.ts `queryKeys.vendorEvents`) so location edits refresh it.
  locations: (eventId: number) => ['vendorEvents', eventId, 'discount-locations'] as const,
}

/**
 * A schedule change moves prices: the event's schedule lists and report,
 * every item's schedule list, and the menu item queries in queries.ts
 * (`queryKeys.menuItems` / `queryKeys.menuItem`), which carry
 * `effective_price` and `active_discount`.
 */
function useInvalidateDiscounts(eventId: number) {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries({ queryKey: discountKeys.all(eventId) })
    queryClient.invalidateQueries({ queryKey: ['discount-schedule'] })
    queryClient.invalidateQueries({ queryKey: ['item-discounts'] })
    queryClient.invalidateQueries({ queryKey: ['menuItems'] })
    queryClient.invalidateQueries({ queryKey: ['menuItem'] })
  }
}

/**
 * Put a saved or archived schedule straight into both cached lists (with and
 * without archived), so every row shows its new state while the invalidated
 * lists refetch. Only rows already listed change: nothing is inserted, and a
 * row the cache already shows archived is never brought back.
 */
function usePatchScheduleLists(eventId: number) {
  const queryClient = useQueryClient()
  return (id: number, patch: (schedule: DiscountSchedule, includeArchived: boolean) => DiscountSchedule | null) => {
    for (const includeArchived of [false, true]) {
      queryClient.setQueryData<DiscountSchedule[]>(discountKeys.schedules(eventId, includeArchived), (old) =>
        old?.flatMap((schedule) => {
          if (schedule.id !== id) return [schedule]
          if (schedule.archived_at) return [schedule]
          const next = patch(schedule, includeArchived)
          return next ? [next] : []
        }),
      )
    }
  }
}

export function useDiscountSchedules(eventId: number, opts?: { includeArchived?: boolean }) {
  const { token } = useAuth()
  const includeArchived = !!opts?.includeArchived
  return useQuery({
    queryKey: discountKeys.schedules(eventId, includeArchived),
    queryFn: () => discountClient.list(token!, eventId, includeArchived),
    enabled: !!token && !!eventId,
    // Keep the list on screen while the archived toggle loads the other one,
    // but never show another event's schedules.
    placeholderData: (previous, previousQuery) => (previousQuery?.queryKey[1] === eventId ? previous : undefined),
  })
}

/**
 * One schedule, fetched again every time a component using it mounts: the
 * editor edits from this (waiting for `isFetchedAfterMount`), never from a
 * cached copy, since a target change sends all three target lists in full
 * and a stale copy would drop targets added since.
 */
export function useDiscountSchedule(scheduleId: number | null | undefined) {
  const { token } = useAuth()
  return useQuery({
    queryKey: discountKeys.schedule(scheduleId ?? 0),
    queryFn: () => discountClient.get(token!, scheduleId!),
    enabled: !!token && !!scheduleId,
    refetchOnMount: 'always',
  })
}

export function useCreateDiscountSchedule(eventId: number) {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  const invalidate = useInvalidateDiscounts(eventId)
  return useMutation({
    mutationFn: (data: DiscountScheduleInput) => discountClient.create(token!, data),
    onSuccess: (schedule: DiscountSchedule) => {
      queryClient.setQueryData(discountKeys.schedule(schedule.id), schedule)
      invalidate()
    },
  })
}

/** Pause/resume is `mutate({ id, data: { is_active } })`. */
export function useUpdateDiscountSchedule(eventId: number) {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  const invalidate = useInvalidateDiscounts(eventId)
  const patchLists = usePatchScheduleLists(eventId)
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<DiscountScheduleInput> }) =>
      discountClient.update(token!, id, data),
    onSuccess: (schedule: DiscountSchedule) => {
      queryClient.setQueryData(discountKeys.schedule(schedule.id), schedule)
      patchLists(schedule.id, (_current, includeArchived) => (schedule.archived_at && !includeArchived ? null : schedule))
      invalidate()
    },
  })
}

/** `mutate(scheduleId)`. Archiving also pauses the schedule. */
export function useArchiveDiscountSchedule(eventId: number) {
  const { token } = useAuth()
  const invalidate = useInvalidateDiscounts(eventId)
  const patchLists = usePatchScheduleLists(eventId)
  return useMutation({
    mutationFn: (scheduleId: number) => discountClient.archive(token!, scheduleId),
    onSuccess: (_: void, scheduleId: number) => {
      const archivedAt = new Date().toISOString()
      patchLists(scheduleId, (current, includeArchived) =>
        includeArchived ? { ...current, archived_at: archivedAt, is_active: false, can_edit: false } : null)
      invalidate()
    },
  })
}

/** Prices a draft without saving it. Nothing is cached or invalidated. */
export function useDiscountPreview() {
  const { token } = useAuth()
  return useMutation({
    mutationFn: (data: DiscountPreviewInput) => discountClient.preview(token!, data),
  })
}

export function useItemDiscounts(itemId: number) {
  const { token } = useAuth()
  return useQuery({
    queryKey: discountKeys.itemDiscounts(itemId),
    queryFn: () => discountClient.itemDiscounts(token!, itemId),
    enabled: !!token && !!itemId,
  })
}

export function useDiscountReport(eventId: number) {
  const { token } = useAuth()
  return useQuery({
    queryKey: discountKeys.report(eventId),
    queryFn: () => discountClient.report(token!, eventId),
    enabled: !!token && !!eventId,
  })
}

export function useDiscountFallbackOrders(eventId: number) {
  const { token } = useAuth()
  return useQuery({
    queryKey: discountKeys.fallbackOrders(eventId),
    queryFn: () => discountClient.fallbackOrders(token!, eventId),
    enabled: !!token && !!eventId,
  })
}

/** Every menu item at the event, unexpanded, for the schedule editor's item picker and preview names. */
export function useDiscountTargetItems(eventId: number) {
  const { token } = useAuth()
  return useQuery({
    queryKey: discountKeys.targetItems(eventId),
    queryFn: () => discountClient.targetItems(token!, eventId),
    enabled: !!token && !!eventId,
    staleTime: 60_000,
  })
}

/**
 * The event's locations with their vendors, to label targets and orders: all
 * of them, or only the viewer's own when they may not list the event's
 * (`discountClient.locations`). A refusal isn't retried.
 */
export function useDiscountLocations(eventId: number) {
  const { token, user } = useAuth()
  return useQuery({
    queryKey: discountKeys.locations(eventId),
    queryFn: () => discountClient.locations(token!, eventId, user?.id),
    enabled: !!token && !!eventId,
    retry: (failureCount, error) => failureCount < 1 && (error as { status?: number }).status !== 403,
  })
}

/** What the requesting user may do with promotions at the event (backend-computed, so it matches what the API enforces). */
export function useDiscountScope(eventId: number) {
  const { token } = useAuth()
  return useQuery({
    queryKey: discountKeys.scope(eventId),
    queryFn: () => discountClient.scope(token!, eventId),
    enabled: !!token && !!eventId,
    staleTime: 60_000,
  })
}

